/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import {
  Modal,
  Tabs,
  Descriptions,
  Avatar,
  Badge,
  Typography,
  Tag,
  Button,
  Space,
  Card,
  message,
} from 'antd';
import { UserOutlined } from '@ant-design/icons';
import TestInterface from './TestInterface';

const { TabPane } = Tabs;
const { Title, Text } = Typography;

const testsData = [
  {
    id: 1,
    name: 'Evaluación de Personalidad',
    description: 'Evalúa rasgos de personalidad y comportamiento',
    duration: '20 minutos',
    questions: [
      {
        id: 1,
        text: '¿Cómo reacciona el candidato ante situaciones de presión o estrés en el trabajo?',
        type: 'open',
      },
      {
        id: 2,
        text: 'Describa cómo el candidato maneja los conflictos con compañeros de trabajo.',
        type: 'open',
      },
      {
        id: 3,
        text: '¿Qué estrategias utiliza el candidato para mantener un equilibrio entre su vida personal y profesional?',
        type: 'open',
      },
      {
        id: 4,
        text: '¿Cómo reacciona el candidato ante la crítica constructiva?',
        type: 'open',
      },
      {
        id: 5,
        text: 'Describa la capacidad del candidato para adaptarse a cambios inesperados.',
        type: 'open',
      },
    ],
  },
  {
    id: 2,
    name: 'Evaluación de Habilidades Técnicas',
    description: 'Evalúa conocimientos técnicos específicos',
    duration: '30 minutos',
    questions: [
      {
        id: 1,
        text: '¿Qué nivel de conocimiento demuestra el candidato en las tecnologías requeridas para el puesto?',
        type: 'open',
      },
      {
        id: 2,
        text: 'Describa la capacidad del candidato para resolver problemas técnicos complejos.',
        type: 'open',
      },
      {
        id: 3,
        text: '¿Cómo evalúa la habilidad del candidato para aprender nuevas tecnologías?',
        type: 'open',
      },
      {
        id: 4,
        text: 'Describa la experiencia del candidato con metodologías ágiles.',
        type: 'open',
      },
      {
        id: 5,
        text: '¿Qué nivel de conocimiento demuestra el candidato en herramientas específicas del sector?',
        type: 'open',
      },
    ],
  },
  {
    id: 3,
    name: 'Evaluación de Liderazgo',
    description: 'Evalúa capacidades de liderazgo y gestión',
    duration: '25 minutos',
    questions: [
      {
        id: 1,
        text: '¿Cómo describiría el estilo de liderazgo del candidato?',
        type: 'open',
      },
      {
        id: 2,
        text: 'Describa la capacidad del candidato para motivar a un equipo.',
        type: 'open',
      },
      {
        id: 3,
        text: '¿Cómo maneja el candidato situaciones donde debe tomar decisiones difíciles?',
        type: 'open',
      },
      {
        id: 4,
        text: 'Describa la habilidad del candidato para delegar tareas y responsabilidades.',
        type: 'open',
      },
      {
        id: 5,
        text: '¿Cómo evalúa la capacidad del candidato para desarrollar a otros miembros del equipo?',
        type: 'open',
      },
    ],
  },
];

export default function CandidateModal({ candidate, isOpen, onClose }) {
  const [activeTest, setActiveTest] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [confirmTest, setConfirmTest] = useState(null); // Nuevo estado para el test a confirmar

  if (!candidate) return null;

  const handleStartTest = (testId) => {
    if (activeTest) {
      message.warning('Debes finalizar la prueba actual antes de iniciar otra.');
      return;
    }
    const test = testsData.find((t) => t.id === testId);
    setConfirmTest(test); // Abre el modal personalizado
  };

  const handleConfirmTest = () => {
    setActiveTest(confirmTest);
    setActiveTab('test');
    message.info(`Prueba "${confirmTest.name}" iniciada`);
    setConfirmTest(null);
  };

  const handleCancelConfirm = () => {
    setConfirmTest(null);
  };

  const handleFinishTest = () => {
    setActiveTest(null);
    setActiveTab('info');
    message.success('Prueba finalizada correctamente.');
  };

  return (
    <>
      <Modal
        open={isOpen}
        onCancel={onClose}
        footer={null}
        width="80%"
        destroyOnClose
        title={<Title level={3}>Información del Candidato</Title>}
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Información" key="info">
            <Space align="start" className="mb-5" size="large">
              <Avatar size={72} icon={<UserOutlined />} />
              <div>
                <Title level={4} style={{ marginBottom: 0 }}>{candidate.nombre}</Title>
                <Text type="secondary">{candidate.email}</Text>
                <div className="mt-2">
                  <Badge status="processing" text={candidate.vacantePlaza} style={{ marginRight: 8 }} />
                  <Badge status="default" text={candidate.estatus} />
                </div>
              </div>
            </Space>

            {/* Información Personal */}
            <Card title="🧍 Información Personal" bordered className="mb-4">
              <Descriptions column={2} size="small" bordered>
                <Descriptions.Item label="Teléfono">{candidate.telefono}</Descriptions.Item>
                <Descriptions.Item label="Fecha de Aplicación">{candidate.fechaAplicacion}</Descriptions.Item>
                <Descriptions.Item label="Experiencia">{candidate.experiencia}</Descriptions.Item>
                <Descriptions.Item label="Escolaridad">{candidate.escolaridad}</Descriptions.Item>
                <Descriptions.Item label="Institución">{candidate.institucion}</Descriptions.Item>
                <Descriptions.Item label="Año de Graduación">{candidate.anioGraduacion}</Descriptions.Item>
                <Descriptions.Item label="CV">
                  <a href={candidate.cvURL} target="_blank" rel="noreferrer">Ver CV</a>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Información de Vacante */}
            <Card title="💼 Información de la Vacante" bordered className="mb-4">
              <Descriptions column={2} size="small" bordered>
                <Descriptions.Item label="Código Vacante">{candidate.vacanteCodigo}</Descriptions.Item>
                <Descriptions.Item label="Plaza">{candidate.vacantePlaza}</Descriptions.Item>
                <Descriptions.Item label="Puesto">{candidate.vacantePuesto}</Descriptions.Item>
                <Descriptions.Item label="Tipo de Empleado">{candidate.vacanteTipoEmpleado}</Descriptions.Item>
                <Descriptions.Item label="Motivo">{candidate.vacanteMotivo}</Descriptions.Item>
                <Descriptions.Item label="Estatus Vacante">{candidate.vacanteEstatus}</Descriptions.Item>
                <Descriptions.Item label="Oficio">{candidate.vacanteOficio}</Descriptions.Item>
                <Descriptions.Item label="Pruebas Asignadas">
                  {candidate.vacantePruebas.map((p) => (
                    <Tag key={p}>{p}</Tag>
                  ))}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Información del Área */}
            <Card title="🏢 Área Solicitante" bordered className="mb-4">
              <Descriptions column={2} size="small" bordered>
                <Descriptions.Item label="Código de Área">{candidate.solicitanteAreaCodigo}</Descriptions.Item>
                <Descriptions.Item label="Nombre de Área">{candidate.solicitanteAreaNombre}</Descriptions.Item>
                <Descriptions.Item label="Nombre del Solicitante">{candidate.solicitanteNombre}</Descriptions.Item>
                <Descriptions.Item label="Puesto del Solicitante">{candidate.solicitantePuesto}</Descriptions.Item>
                <Descriptions.Item label="Folio Solicitud">{candidate.solicitanteFolio}</Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Pruebas Disponibles */}
            <Card
              title="🧪 Pruebas Disponibles"
              style={{ background: '#f5f5f5', border: 'none', marginTop: 24 }}
              bodyStyle={{ padding: 16 }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {testsData.map((test) => {
                  const isCompleted = candidate.completedTestIds?.includes(test.id);
                  return (
                    <div
                      key={test.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 0',
                        borderBottom: '1px solid #e0e0e0',
                        minHeight: 40,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 500 }}>{test.name}</span>
                        {isCompleted && <Tag color="green">Completada</Tag>}
                      </div>
                      <Button
                        disabled={!!activeTest}
                        onClick={() => handleStartTest(test.id)}
                        style={{
                          minWidth: 120,
                          textAlign: 'center',
                          background: '#235161',
                          color: '#fff',
                          border: 'none',
                          fontWeight: 500,
                          transition: 'background 0.2s',
                        }}
                        className="btn-prueba-disponible"
                      >
                        {isCompleted ? 'Ver resultados' : 'Iniciar'}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </Card>
          </TabPane>

          <TabPane
            tab={activeTest ? `Prueba: ${activeTest.name}` : 'Prueba'}
            key="test"
            disabled={!activeTest}
          >
            {activeTest && (
              <TestInterface
                test={activeTest}
                candidate={candidate}
                onFinish={handleFinishTest}
              />
            )}
          </TabPane>
        </Tabs>
      </Modal>
      {/* Modal personalizado de confirmación de inicio de prueba */}
      <Modal
        open={!!confirmTest}
        onCancel={handleCancelConfirm}
        onOk={handleConfirmTest}
        okText="Iniciar"
        cancelText="Cancelar"
        title={confirmTest ? (
          <Title level={4}>
            ¿Deseas iniciar la prueba
            <span style={{ color: '#1890ff' }}>
              &nbsp;&quot;
              {confirmTest.name}
              &quot;
            </span>
            ?
          </Title>
        ) : null}
        footer={[
          <Button key="cancel" onClick={handleCancelConfirm}>Cancelar</Button>,
          <Button
            key="ok"
            type="primary"
            onClick={handleConfirmTest}
            style={{
              minWidth: 120,
              textAlign: 'center',
              background: '#235161',
              color: '#fff',
              border: 'none',
              fontWeight: 500,
              transition: 'background 0.2s',
            }}
          >
            Iniciar
          </Button>,
        ]}
        zIndex={2001} // Asegura que esté por encima del modal principal (por defecto 1000)
        mask
        maskClosable={false}
      >
        {confirmTest && (
          <>
            <p>
              <strong>
                Una vez iniciada, no podrás comenzar otra prueba hasta que finalices esta.
              </strong>
            </p>
            <p>
              <strong>Duración estimada:</strong>
              <span>
                {confirmTest.duration}
              </span>
            </p>
            <p>
              <strong>Descripción:</strong>
              <span>
                {confirmTest.description}
              </span>
            </p>
          </>
        )}
      </Modal>
      <style>
        {`
          .btn-prueba-disponible:not([disabled]):hover {
            background: #16364a !important;
            color: #fff !important;
          }
        `}
      </style>
    </>
  );
}
